import {REVIEW_STATE, REJECT_REASON, REQUEST_SOURCE} from './reference'
import {nowDate} from './clock'

/**
 * Weight Freeze (v2) — MerchantWeightFreezeRequest (the backend document) ⇄ this module's request
 * shape (data/noberoFreeze.js REQUESTS). Pure, import-light, unit-tested; the only place the two
 * vocabularies meet.
 *
 * The panel keys three things off STRING ENUMS it already owns — `kind` ('approved' | 'review' |
 * 'rejected'), `state` (REVIEW_STATE) and `reason` (REJECT_REASON) — so every backend status is
 * translated to those here, once, and the screens stay untouched.
 */

export const LEVEL_FROM_API = {SKU: 'SKU', SUB_CATEGORY: 'Sub-category', COMBO: 'Combo'}
export const LEVEL_TO_API = {SKU: 'SKU', 'Sub-category': 'SUB_CATEGORY', Combo: 'COMBO'}

const REASON_FROM_API = {
  CONTRADICTORY_DATA: REJECT_REASON.CONTRADICTORY,
  COURIER_CONSTRAINT: REJECT_REASON.COURIER_SIDE,
  INADEQUATE_AFTER_LIMIT: REJECT_REASON.INADEQUATE,
  OTHER: 'Declined by Prozo',
}

/** 'closed' = shown in no tab: a superseded approval is history the newer approval tells better. */
export function kindOf(status) {
  switch (status) {
    case 'APPROVED': return 'approved'
    case 'REJECTED':
    case 'WITHDRAWN':
    case 'PROPOSAL_DECLINED': return 'rejected'
    case 'AWAITING_REVIEW':
    case 'ADDITIONAL_DATA_REQUESTED':
    case 'DATA_SUBMITTED':
    case 'PROPOSED': return 'review'
    default: return 'closed'
  }
}

export function stateOf(status) {
  switch (status) {
    case 'AWAITING_REVIEW': return REVIEW_STATE.AWAITING
    case 'ADDITIONAL_DATA_REQUESTED': return REVIEW_STATE.ADDITIONAL
    case 'DATA_SUBMITTED': return REVIEW_STATE.SUBMITTED
    case 'PROPOSED': return REVIEW_STATE.PROPOSED
    default: return undefined
  }
}

/** The rejected tab's one-line reason, for every terminal-but-not-approved status. */
export function reasonOf(r) {
  switch (r.status) {
    case 'REJECTED': return REASON_FROM_API[r.rejectReason] || REASON_FROM_API.OTHER
    case 'WITHDRAWN': return r.source === REQUEST_SOURCE.PROZO_PROPOSED ? 'Proposal withdrawn by Prozo' : 'Withdrawn by you'
    case 'PROPOSAL_DECLINED': return "You declined Prozo's proposal"
    default: return undefined
  }
}

const day = (v) => (v == null ? null : new Date(v).toISOString().slice(0, 10))

/** Whole billing cycles (calendar months) since the effective date, never negative. */
export function cyclesElapsed(effectiveFrom, now = nowDate()) {
  if (!effectiveFrom) return 0
  const d = new Date(effectiveFrom)
  if (Number.isNaN(d.getTime())) return 0
  let months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth())
  if (now.getDate() < d.getDate()) months -= 1
  return Math.max(0, months)
}

/** Backend evidence (S3 refs with signed links) → the drawer's {video, images[]} placeholder shape. */
function evidenceFilesOf(ev) {
  if (!ev) return null
  const video = ev.video && ev.video.signedLink ? {name: 'Packing video', url: ev.video.signedLink} : null
  const images = (ev.images || []).filter((s) => s && s.signedLink)
    .map((s, i) => ({slot: `IMG${i + 1}`, name: `Photograph ${i + 1}`, url: s.signedLink}))
  if (!video && !images.length) return null
  return {video, images}
}

/** One backend request → one panel request. `id` is the human reference the screens print; `backendId` is the key the API takes. */
export function mapRequestFromApi(r, now = nowDate()) {
  if (!r) return null
  const kind = kindOf(r.status)
  const ev = r.evidence || {}
  return {
    id: r.requestRef || r.id,
    backendId: r.id,
    status: r.status,
    kind,
    state: stateOf(r.status),
    reason: reasonOf(r),
    level: LEVEL_FROM_API[r.level] || r.level,
    subject: r.subject || '',
    covers: r.covers || [],
    deadKg: r.proposedDeadKg != null ? r.proposedDeadKg : 0,
    volKg: r.proposedVolKg != null ? r.proposedVolKg : 0,
    chargeKg: r.proposedChargeKg != null ? r.proposedChargeKg : 0,
    packId: r.packId || null,
    packLabel: r.packLabel || null,
    declaredDims: r.declaredDims ? [r.declaredDims.l, r.declaredDims.b, r.declaredDims.h] : null,
    evidence: (ev.dwsAwbs || []).length,
    evidenceAwbs: ev.dwsAwbs || [],
    evidenceFiles: evidenceFilesOf(ev),
    remarks: r.remarks || '',
    raisedBy: r.raisedBy || '',
    raisedOn: day(r.raisedAt),
    round: r.round || 1,
    submissions: r.submissions || 0,
    history: (r.history || []).map((e) => ({
      date: day(e.at),
      actor: e.actor || 'System',
      round: e.round || 1,
      kind: e.kind,
      text: e.text || '',
      ...(Array.isArray(e.requestedItems) && e.requestedItems.length ? {requestedItems: e.requestedItems} : {}),
    })),
    approvedBy: kind === 'approved' ? (r.decidedBy || 'Prozo') : undefined,
    effectiveFrom: kind === 'approved' ? day(r.effectiveFrom || r.decidedAt) : undefined,
    cyclesElapsed: kind === 'approved' ? cyclesElapsed(r.effectiveFrom || r.decidedAt, now) : undefined,
    decidedOn: day(r.decidedAt),
    decisionRemark: r.decisionRemark || null,
    approvalNotice: r.approvalNotice || null,
    billingActive: !!r.billingActive,
    source: r.source || REQUEST_SOURCE.MERCHANT,
    proposalBasis: r.proposalBasis || null,
  }
}

/**
 * The raise drawers' payload → the backend RaiseBody. `declaredDims` arrives as the [l, b, h] triple
 * the drawers derive volumetric from; `packLabel` is resolved by the caller from the packs it has.
 */
export function toRaiseBody(p, {packLabel, packSelfWeightG} = {}) {
  const dims = Array.isArray(p.declaredDims) && p.declaredDims.length === 3
    ? {l: Number(p.declaredDims[0]), b: Number(p.declaredDims[1]), h: Number(p.declaredDims[2])}
    : (p.declaredDims && typeof p.declaredDims === 'object' ? p.declaredDims : null)
  return {
    level: LEVEL_TO_API[p.level] || p.level,
    subject: p.subject,
    covers: p.covers || [],
    deadKg: Number(p.deadKg),
    volKg: Number(p.volKg),
    chargeKg: Number(p.chargeKg),
    declaredDims: dims,
    packId: p.packId,
    packLabel: packLabel || null,
    packSelfWeightG: packSelfWeightG == null ? null : packSelfWeightG,
    // The drawers gate submission on the declaration checkbox (GAP.DECLARATION), so a payload that
    // reached this point carries an accepted declaration.
    declarationAccepted: true,
    remarks: p.remarks || null,
    dwsAwbs: p.evidenceAwbs || [],
  }
}
